// src/components/DocumentViewer.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion } from 'framer-motion';
import { FileText, Clock, User, Hash, GitCompare, X, Copy, Lock } from 'lucide-react';
import { Document as DocType, GitCommit } from '../App';
import * as Diff from 'diff';
import TxtRayDiffViewer from './TxtRayDiffViewer';
import './styles.css';

interface DocumentViewerProps {
  document: DocType;
  content: string;
  isEditMode: boolean;
  isHistorical: boolean;
  selectedCommit?: GitCommit | null;
  compareContent?: string | null;
  compareDocument?: DocType | null;
  onContentChange: (content: string) => void;
  onDocumentLink: (targetPath: string, section?: string) => void;
  isReadOnly?: boolean;
  onClone?: () => void;
  onProtect?: () => void;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  document,
  content,
  isEditMode,
  isHistorical,
  selectedCommit,
  compareContent,
  compareDocument,
  onContentChange,
  onDocumentLink,
  isReadOnly = false,
  onClone = () => {},
  onProtect = () => {}
}) => {
  const [processedContent, setProcessedContent] = useState(content);
  const [highlightedDiffs, setHighlightedDiffs] = useState<any[]>([]);
  const [showDiffViewer, setShowDiffViewer] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const viewerRef = useRef<HTMLDivElement>(null);

  // Process custom link syntax
  useEffect(() => {
    processCustomLinks();
  }, [content]);

  const processCustomLinks = () => {
    // Process [[Link Text -> target.md#section]] syntax
    const linkPattern = /\[\[(.*?)\s*->\s*(.*?)\]\]/g;
    let processed = content;
    
    const matches = [...content.matchAll(linkPattern)];
    matches.forEach(match => {
      const [fullMatch, linkText, target] = match;
      const [targetDoc, section] = target.split('#');
      
      // Convert to Markdown link with data attributes - use # href to prevent navigation
      const mdLink = `[${linkText}](#timefiles-link "${targetDoc}${section ? '#' + section : ''}")`;
      processed = processed.replace(fullMatch, mdLink);
    });
    
    setProcessedContent(processed);
  };

  // Handle link clicks in rendered markdown
  useEffect(() => {
    if (!isEditMode && viewerRef.current) {
      const links = viewerRef.current.querySelectorAll('a[href="#timefiles-link"]');
      
      links.forEach(link => {
        link.addEventListener('click', handleLinkClick);
      });
      
      return () => {
        links.forEach(link => {
          link.removeEventListener('click', handleLinkClick);
        });
      };
    }
  }, [isEditMode, processedContent]);

  const handleLinkClick = (e: Event) => {
    e.preventDefault();
    const target = e.target as HTMLAnchorElement;
    const title = target.getAttribute('title');
    
    if (title) {
      const [targetDoc, section] = title.split('#');
      
      // Animate the link before navigation
      animateLinkTransition(target, () => {
        onDocumentLink(targetDoc, section);
      });
    }
  };

  // Zoom animation for document links
  const animateLinkTransition = (element: HTMLElement, callback: () => void) => {
    const rect = element.getBoundingClientRect();
    const clone = element.cloneNode(true) as HTMLElement;
    
    // Create overlay
    const overlay = window.document.createElement('div');
    overlay.className = 'link-transition-overlay';
    window.document.body.appendChild(overlay);
    
    // Position clone at link location
    clone.style.position = 'fixed';
    clone.style.left = `${rect.left}px`;
    clone.style.top = `${rect.top}px`;
    clone.style.zIndex = '10000';
    clone.className = 'link-transition-element';
    window.document.body.appendChild(clone);
    
    // Animate to full screen
    requestAnimationFrame(() => {
      clone.style.transform = 'scale(50)';
      clone.style.opacity = '0';
      overlay.style.opacity = '1';
    });
    
    setTimeout(() => {
      window.document.body.removeChild(clone);
      window.document.body.removeChild(overlay);
      callback();
    }, 300);
  };

  // Auto-resize textarea
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onContentChange(e.target.value);
    
    // Auto-resize
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  // Scroll to section if specified
  const scrollToSection = (sectionId: string) => {
    const element = window.document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Highlight the section briefly
      element.classList.add('section-highlight');
      setTimeout(() => {
        element.classList.remove('section-highlight');
      }, 2000);
    }
  };

  // Custom components for ReactMarkdown
  const markdownComponents = {
    h1: ({ children, ...props }: any) => {
      const id = children.toString().toLowerCase().replace(/\s+/g, '-');
      return <h1 id={id} {...props}>{children}</h1>;
    },
    h2: ({ children, ...props }: any) => {
      const id = children.toString().toLowerCase().replace(/\s+/g, '-');
      return <h2 id={id} {...props}>{children}</h2>;
    },
    h3: ({ children, ...props }: any) => {
      const id = children.toString().toLowerCase().replace(/\s+/g, '-');
      return <h3 id={id} {...props}>{children}</h3>;
    },
    code: ({ inline, className, children, ...props }: any) => {
      const match = /language-(\w+)/.exec(className || '');
      
      if (!inline && match) {
        return (
          <div className="code-block">
            <div className="code-lang">{match[1]}</div>
            <pre className={className}>
              <code {...props}>{children}</code>
            </pre>
          </div>
        );
      }
      
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
    a: ({ href, children, ...props }: any) => {
      // Handle external links
      if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
        return (
          <a 
            href={href} 
            target="_blank" 
            rel="noopener noreferrer"
            className="external-link"
            {...props}
          >
            {children}
          </a>
        );
      }
      
      // Internal document links
      return (
        <a 
          href={href} 
          className="document-link"
          {...props}
        >
          {children}
        </a>
      );
    },
    // Custom rendering for diff highlighting
    p: ({ children, ...props }: any) => {
      if (typeof children === 'string' && highlightedDiffs.length > 0) {
        // Check if this paragraph contains diffs
        const hasDiff = highlightedDiffs.some(diff => 
          children.includes(diff.value)
        );
        
        if (hasDiff) {
          return (
            <p {...props}>
              {highlightedDiffs.map((part, index) => {
                const className = part.added ? 'diff-added' : 
                                 part.removed ? 'diff-removed' : '';
                return (
                  <span key={index} className={className}>
                    {part.value}
                  </span>
                );
              })}
            </p>
          );
        }
      }
      
      return <p {...props}>{children}</p>;
    }
  };

  return (
    <div className="document-viewer">
      {/* Document Header */}
      <div className="document-header">
        <div className="document-info">
          <FileText size={20} />
          <h2>{document.name}</h2>
          {document.gitStatus && (
            <span className={`git-status git-status-${document.gitStatus}`}>
              {document.gitStatus}
            </span>
          )}
        </div>
        
        <div className="document-actions">
          {/* Clone button for read-only/imported files */}
          {(isReadOnly || isHistorical || 
            document.path.endsWith('.txt') || 
            document.path.endsWith('.pdf') || 
            document.path.endsWith('.docx')) && (
            <button
              onClick={onClone}
              className="btn-icon"
              title="Clone to create editable copy"
            >
              <Copy size={18} />
              <span>Clone</span>
            </button>
          )}
          
          {/* Password Protection */}
          <button
            onClick={onProtect}
            className="btn-icon"
            title="Protect document with password"
          >
            <Lock size={18} />
            <span>Protect</span>
          </button>
          
          {compareContent && (
            <button
              onClick={() => setShowDiffViewer(!showDiffViewer)}
              className="diff-toggle-button"
              title="Toggle diff view"
            >
              <GitCompare size={18} />
              <span>Compare</span>
            </button>
          )}
        </div>
        
        {isHistorical && selectedCommit && (
          <div className="historical-info">
            <Clock size={16} />
            <span>Viewing version from {new Date(selectedCommit.date).toLocaleString()}</span>
            <User size={16} />
            <span>{selectedCommit.author}</span>
            <Hash size={16} />
            <span>{selectedCommit.hash.substring(0, 7)}</span>
          </div>
        )}
      </div>

      {/* Diff Viewer Overlay */}
      {showDiffViewer && compareContent && (
        <div className="diff-viewer-overlay">
          <TxtRayDiffViewer
            text1={compareContent}
            text2={content}
            title1={compareDocument?.name || 'Original'}
            title2={document.name}
            onClose={() => setShowDiffViewer(false)}
            className="diff-viewer-container"
          />
        </div>
      )}

      {/* Document Content */}
      <div className="document-content">
        {isEditMode ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="editor-container"
          >
            <textarea
              ref={textareaRef}
              className="markdown-editor"
              value={content}
              onChange={handleTextareaChange}
              spellCheck={true}
              placeholder="Write your markdown here..."
            />
          </motion.div>
        ) : (
          <motion.div
            ref={viewerRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="markdown-viewer"
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {processedContent}
            </ReactMarkdown>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default DocumentViewer;